import { useState } from 'react';
import { ArrowLeft, CircleDollarSign, MailCheck, Receipt, RefreshCw, Send, ShoppingBag, UserCheck } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useCampaignScope } from '../model/campaign-scope';
import { useCampaignReport } from '../api/use-campaign-report';
import { PageHeader } from '@/shared/components/page-header';
import { KpiCard } from '@/shared/components/kpi-card';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Skeleton } from '@/shared/ui/skeleton';
import { brl, decimal } from '@/shared/lib/format';
import { DispatchesChart, FunnelBars, PurchaseLagChart, RevenueChart } from './campaign-report-charts';
import { BuyersTable, DispatchesTable } from './campaign-report-tables';

type Period = '30' | '90' | '180';

const PERIODS = [
  { value: '30' as const, label: '30 dias' },
  { value: '90' as const, label: '90 dias' },
  { value: '180' as const, label: '180 dias' },
];

function ChartCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function CampaignResultsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { idPassbook, listPath, organizationName } = useCampaignScope();
  const [period, setPeriod] = useState<Period>('90');
  const { data, isLoading, isFetching, error, refetch } = useCampaignReport(idPassbook, campaignId, Number(period));

  const totals = data?.totals;
  const loading = isLoading || !data;

  return (
    <>
      <PageHeader
        title="Resultados da campanha"
        hideMetaOnMobile
        description={data?.campaign.nome ? `${data.campaign.nome} — ${organizationName}` : organizationName}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <SegmentedControl value={period} onChange={setPeriod} options={PERIODS} size="sm" />
            <Button variant="secondary" size="icon" onClick={() => void refetch()} disabled={isFetching} aria-label="Atualizar">
              <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            </Button>
            <Button variant="secondary" asChild>
              <Link to={listPath}>
                <ArrowLeft className="size-4" /> Voltar
              </Link>
            </Button>
          </div>
        }
      />

      {error && !data ? (
        <div className="glass flex h-48 items-center justify-center rounded-2xl p-6 text-sm text-destructive">
          {(error as Error).message || 'Resultados indisponíveis'}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
            <KpiCard
              index={0}
              label="Disparos"
              icon={Send}
              loading={loading}
              value={decimal(totals?.dispatches)}
              hint="Quantas vezes a campanha rodou no período."
            />
            <KpiCard
              index={1}
              label="Entregues"
              icon={MailCheck}
              loading={loading}
              value={decimal(totals?.delivered)}
              hint={`${decimal(totals?.deliveryRate, 1)}% de ${decimal(totals?.sent)} envios.`}
            />
            <KpiCard
              index={2}
              label="Compraram"
              icon={UserCheck}
              loading={loading}
              value={decimal(totals?.buyers)}
              hint={`${decimal(totals?.conversionRate, 1)}% das ${decimal(totals?.recipients)} pessoas que receberam.`}
            />
            <KpiCard
              index={3}
              label="Pedidos"
              icon={ShoppingBag}
              loading={loading}
              value={decimal(totals?.orders)}
              hint="Compras feitas por quem recebeu, dentro da janela."
            />
            <KpiCard
              index={4}
              label="Receita"
              icon={CircleDollarSign}
              loading={loading}
              value={brl(totals?.revenue)}
              hint="Soma do valor das compras atribuídas à campanha."
            />
            <KpiCard
              index={5}
              label="Ticket médio"
              icon={Receipt}
              loading={loading}
              value={brl(totals?.avgTicket)}
            />
          </div>

          {data ? (
            <p className="text-xs text-muted-foreground">
              Conta como resultado da campanha a compra feita até {data.windowDays}{' '}
              {data.windowDays === 1 ? 'dia' : 'dias'} depois do disparo recebido. Dados de hoje, direto do banco
              (atualizado às {new Date(data.generatedAt).toLocaleTimeString('pt-BR', { timeStyle: 'short' })}).
            </p>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Do envio à compra" description="Quantas pessoas avançam em cada etapa.">
              {data ? <FunnelBars totals={data.totals} /> : <Skeleton className="h-60 w-full rounded-xl" />}
            </ChartCard>
            <ChartCard title="Quando elas compram" description="Dias entre o disparo e a compra.">
              {data ? <PurchaseLagChart lag={data.purchaseLag} /> : <Skeleton className="h-60 w-full rounded-xl" />}
            </ChartCard>
            <ChartCard title="Entregas por dia">
              {data ? <DispatchesChart daily={data.daily} /> : <Skeleton className="h-60 w-full rounded-xl" />}
            </ChartCard>
            <ChartCard title="Receita atribuída por dia">
              {data ? <RevenueChart daily={data.daily} /> : <Skeleton className="h-60 w-full rounded-xl" />}
            </ChartCard>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Clientes que compraram</CardTitle>
              <CardDescription>Clique no nome para abrir o cliente em Leads.</CardDescription>
            </CardHeader>
            <CardContent>
              {data ? (
                <BuyersTable buyers={data.buyers} truncated={data.buyersTruncated} idPassbook={idPassbook} />
              ) : (
                <Skeleton className="h-64 w-full rounded-xl" />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico de disparos</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              {data ? <DispatchesTable dispatches={data.dispatches} /> : <Skeleton className="mx-5 h-40 rounded-xl" />}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
